import React, { useRef, useState } from 'react';
import { addDays, addMinutes, isAfter } from 'date-fns';
import { useIsomorphicLayoutEffect, useUpdateEffect } from 'react-use';
import { styled } from 'styled-components';
import { useMeasure } from '../../../../../play-core/hooks/use-measure';
import { Colors } from '../../../../../play-core/styles/Colors';
import { hideScrollbar } from '../../../../../play-core/styles/style-helper';
import { times } from '../../../../../play-core/utils/times';
import { parseToTime, shortTimeFormat } from '../../../utils/date-formatter';
import { NowArrow } from '../NowArrow';
import { ShadowOverlay } from '../ShadowOverlay';
import { TimeJumpButtons } from '../TimeJumpButtons';
import { TimelineMarker } from '../TimelineMarker';
import { calculateStickyPosition } from '../tv-guide-helper';
import { getTimePosition, listTimelineHeight, minutesPerDay, startOfTvDay } from '../tv-guide-types';
import { useNowMarkerPosition } from '../use-now-marker-position';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const nowLineWidth = 2;
export const ListTimeline = ({
  time,
  date,
  onChange,
  stickyTopPosition
}) => {
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const {
    width
  } = useMeasure(scrollRef);
  const pixelsPerMinute = 1.5;
  const markerInterval = 60;
  const markerCount = minutesPerDay / markerInterval + 1;
  const halfScrollWidth = Math.round(width / 2);
  const now = useNowMarkerPosition(date, pixelsPerMinute, 0);

  // Notify change listener when scroll position changes
  useUpdateEffect(() => {
    const dateTimeString = calculateTimeFromPosition(scrollPosition, date, pixelsPerMinute);
    const newTime = shortTimeFormat(dateTimeString);
    onChange(newTime);
  }, [scrollPosition, date, onChange]);

  // Set time
  useIsomorphicLayoutEffect(() => {
    if (scrollRef?.current) {
      const timeValue = parseToTime(time);
      const newScrollPosition = getTimePosition(timeValue, pixelsPerMinute, 0);
      scrollRef.current.scrollLeft = newScrollPosition;
      setScrollPosition(newScrollPosition);
    }
  }, [time]);
  return /*#__PURE__*/_jsxs(Layout, {
    stickyTopPosition: stickyTopPosition,
    children: [/*#__PURE__*/_jsxs(ScrollContainer, {
      ref: scrollRef,
      onScroll: ({
        currentTarget
      }) => setScrollPosition(currentTarget.scrollLeft),
      children: [times(markerCount, index => /*#__PURE__*/_jsx(TimelineMarker, {
        minutes: index * markerInterval,
        left: index * markerInterval * pixelsPerMinute + halfScrollWidth,
        top: 28
      }, index)), /*#__PURE__*/_jsx(SpacerRight, {
        style: {
          left: `${(markerCount - 1) * markerInterval * pixelsPerMinute + halfScrollWidth}px`,
          width: `${halfScrollWidth}px`
        }
      }), now && /*#__PURE__*/_jsx(NowLine, {
        style: {
          left: `${now + halfScrollWidth - nowLineWidth / 2}px`
        }
      })]
    }), /*#__PURE__*/_jsx(NowArrow, {
      top: listTimelineHeight,
      centerHorizontally: true
    }), /*#__PURE__*/_jsx(ShadowOverlay, {
      position: "Left",
      visible: true
    }), /*#__PURE__*/_jsx(ShadowOverlay, {
      position: "Right",
      visible: true
    }), /*#__PURE__*/_jsx(TimeJumpButtons, {
      scrollPosition: scrollPosition,
      scrollRef: scrollRef,
      date: date,
      pixelsPerMinute: pixelsPerMinute,
      left: 0,
      top: listTimelineHeight,
      isListMode: true
    })]
  });
};
const Layout = styled.div.withConfig({
  displayName: "ListTimeline__Layout",
  componentId: "sc-1z0d04k-0"
})(["", " background-color:", ";"], ({
  stickyTopPosition
}) => calculateStickyPosition(stickyTopPosition), Colors.GREY_1a1a1a);
const ScrollContainer = styled.div.withConfig({
  displayName: "ListTimeline__ScrollContainer",
  componentId: "sc-1z0d04k-1"
})(["height:", "px;position:relative;display:flex;overflow-y:auto;", ";"], listTimelineHeight, hideScrollbar());
const SpacerRight = styled.div.withConfig({
  displayName: "ListTimeline__SpacerRight",
  componentId: "sc-1z0d04k-2"
})(["position:absolute;top:28px;height:1px;"]);
const NowLine = styled.div.withConfig({
  displayName: "ListTimeline__NowLine",
  componentId: "sc-1z0d04k-3"
})(["position:absolute;top:46px;height:18px;width:", "px;background-color:", ";"], nowLineWidth, Colors.WHITE);
const calculateTimeFromPosition = (scrollX, date, pixelsPerMinute) => {
  const selectedTime = addMinutes(startOfTvDay(date), Math.max(scrollX, 0) / pixelsPerMinute);
  const startOfTvDayTomorrow = addMinutes(addDays(startOfTvDay(date), 1), -1);
  return isAfter(selectedTime, startOfTvDayTomorrow) ? startOfTvDayTomorrow : selectedTime;
};